import { Plus } from 'lucide-react';
import React, { useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/_shared/components/ui/button';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/_shared/components/ui/sidebar';
import { Toaster } from '@/_shared/components/ui/sonner';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { FeatureFlagsProvider } from '@/_shared/feature-flags/FeatureFlagsContext';
import { useAuth } from '@/_shared/hooks/useAuth';

import { AppSidebar } from './AppSidebar';

class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  override render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

function ErrorFallback(): React.ReactElement {
  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-foreground">Something went wrong</h2>
        <p className="mt-1 text-sm text-muted-foreground">Try refreshing the page.</p>
      </div>
    </div>
  );
}

function LocationKeyedErrorBoundary({ children }: { children: React.ReactNode }): React.ReactElement {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname} fallback={<ErrorFallback />}>
      {children}
    </ErrorBoundary>
  );
}

export function AppLayout(): React.ReactElement {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { wp } = useWorkspace();

  useEffect(() => {
    const pendingInvite = sessionStorage.getItem('pending_invite');
    if (pendingInvite && user) {
      sessionStorage.removeItem('pending_invite');
      void navigate(`/invite/${pendingInvite}`);
    }
  }, [user, navigate]);

  return (
    <FeatureFlagsProvider>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset className="h-svh overflow-hidden bg-[radial-gradient(circle_at_top_left,rgba(16,94,213,0.08),transparent_26%),linear-gradient(180deg,#f8fbff_0%,#f4f7fc_100%)]">
          <div className="flex items-center justify-between border-b border-border/70 bg-white/70 px-3 py-3 backdrop-blur md:hidden">
            <SidebarTrigger aria-label="Open navigation menu" />
            <Button variant="ghost" size="icon" asChild>
              <Link to={wp()} aria-label="New chat">
                <Plus className="size-4" />
              </Link>
            </Button>
          </div>
          <Toaster />
          <div className="flex min-h-0 flex-1 p-3 md:p-5">
            <div className="flex min-h-0 flex-1 overflow-hidden rounded-[1.75rem] border border-[var(--shell-border)] bg-white/90 shadow-[var(--shell-shadow)] backdrop-blur-xl">
              <LocationKeyedErrorBoundary>
                <Outlet />
              </LocationKeyedErrorBoundary>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </FeatureFlagsProvider>
  );
}
